import React, { useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import api from "common/api";
import ExpandedApplication from "./ExpandedApplication";
import { BsPlusCircle } from "react-icons/bs";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import DeleteApplicationConfirmation from "./DeleteApplicationConfirmation";
import { toast } from "react-toastify";
import AddApplicationToCourseModal from "./AddApplicationToCourseModal";
import { MdClear } from "react-icons/md";
import Spinner from "../../spinner";
import usePageTitle from "common/usePageTitle";

interface IApplication {
  id: number;
  first_name: string;
  last_name: string;
  school: string;
  phone_number: string;
  parent_phone_number: string;
  email: string;
  parent_email: string;
  subjects: { id: number; name: string }[];
  remark: string;
  status: string;
  created_at: string;
  updated_at: string;
  applicant: {
    user_id: string;
  };
}

interface IAPIResponse {
  count: number;
  next: string;
  previous: string;
  results: IApplication[];
}

const MaturaApplicaitonsListPage = () => {
  const [data, setData] = useState<IApplication[]>([]);
  const [courses, setCourses] = useState([]);
  const [currentApplication, setCurrentApplication] = useState<IApplication>(
    {} as IApplication,
  );
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  usePageTitle("Prijave admin");

  const fetchData = useCallback(
    async (offset: number, limit: number, search: string, status: string) => {
      setLoading(true);
      try {
        const response = await api.get<IAPIResponse>("/applications/", {
          params: {
            limit: limit,
            offset: offset,
            search: search,
            status: status,
          },
        });
        setData(response.data.results);
        setTotalRows(response.data.count);
      } catch (error) {
        console.error("Error fetching data: ", error);
        toast.error("Greška prilikom dohvaćanja prijava");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData(offset, perPage, search, status);
  }, [fetchData, offset, perPage, search, status]);

  useEffect(() => {
    async function fetchCourses() {
      try {
        const response = await api.get("/courses/");
        setCourses(response.data);
      } catch (error) {
        toast.error("Greška prilikom dohvaćanja grupa");
        console.error(error);
      }
    }

    fetchCourses();
  }, []);

  const deleteApplication = async (id: number) => {
    try {
      await api.delete(`/applications/${id}/`);
      toast.success("Prijava uspješno obrisana");
      setData(data.filter((application) => application.id !== id));
    } catch (error) {
      console.error("Error deleting application: ", error);
      toast.error("Greška prilikom brisanja prijave");
    }
  };

  const columns: any = [
    {
      name: "Ime",
      selector: (row: IApplication) => row.first_name,
      sortable: true,
    },
    {
      name: "Prezime",
      selector: (row: IApplication) => row.last_name,
      sortable: true,
    },
    {
      name: "Škola",
      selector: (row: IApplication) => row.school,
      sortable: true,
    },
    {
      name: "Predmeti",
      selector: (row: IApplication) => row.subjects,
      grow: 2,
      cell: (row: any) => (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "4px",
          }}
        >
          {row.subjects.map((subj: any) => (
            <span
              key={subj.id}
              style={{
                padding: "4px 8px",
                backgroundColor: "#007BFF",
                color: "white",
                borderRadius: "5px",
              }}
            >
              {subj.name}
            </span>
          ))}
        </div>
      ),
    },
    {
      button: true,
      cell: (row: any) => {
        return (
          <>
            <BsPlusCircle
              className="me-3"
              style={{ color: "darkgreen", cursor: "pointer" }}
              onClick={() => {
                setCurrentApplication(row);
                setShowAddModal(true);
              }}
              title={"Dodaj grupi priprema"}
            />

            <DeleteApplicationConfirmation
              onDeleteConfirm={deleteApplication}
              id={row.id}
            />
          </>
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    const newOffset = (page - 1) * perPage;
    setOffset(newOffset);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    const newOffset = (page - 1) * newPerPage;
    setPerPage(newPerPage);
    setOffset(newOffset);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setOffset(0); // Reset to the first page
  };

  return (
    <Row>
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Prijave na pripreme</h1>
        </Col>

        <Col xs="auto" xl={5}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Pretraži učenika..."
              value={search}
              onChange={handleSearch}
            />
            {search && (
              <InputGroup.Text
                style={{ cursor: "pointer" }}
                onClick={() => setSearch("")}
                title="Obriši pretragu"
              >
                <MdClear />
              </InputGroup.Text>
            )}
          </InputGroup>
        </Col>
      </Row>

      <DataTable
        // title="Lista prijava za maturu"
        columns={columns}
        data={data}
        progressPending={loading}
        progressComponent={<Spinner />}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={perPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        expandableRows
        expandableRowsComponent={ExpandedApplication}
        noDataComponent="Ne postoje prijave"
      />

      <AddApplicationToCourseModal
        show={showAddModal}
        setShow={setShowAddModal}
        application={currentApplication}
        courses={courses}
      />
    </Row>
  );
};

export default MaturaApplicaitonsListPage;
